/**
 * HelpTooltip — the shared, metadata-driven instructional tooltip.
 *
 * Given a stable helpTopicId it reads the visible copy from the helpTopics
 * registry (app/javascript/utils/helpTopics.js) and renders:
 *   - the concise visible tooltip text, and
 *   - when the topic has an aiQuestion, a small Counterintelligence help
 *     control (round CI badge + "?") in the bottom-right footer. One combined
 *     semantic button — selecting it closes the tooltip, opens the existing
 *     Counterintelligence tray and submits the topic's authored question with
 *     structured context (never the raw tooltip prose as the question).
 *
 * Layering: the bubble renders through a portal to document.body at a fixed
 * z-index above the CI drawer (4001) and the sidebar (1101), clamped inside
 * the viewport — so it is never clipped by cards, sidebar, or an ancestor's
 * overflow/stacking context. It closes before the tray opens and never
 * reopens afterwards.
 *
 * Interaction: hover (desktop), focus (keyboard), or tap (touch) opens it;
 * Escape or an outside tap closes it. Tab while open moves focus to the CI
 * help button; focus never gets trapped inside the tooltip.
 *
 * Two trigger shapes share one hook:
 *   <HelpTooltip helpTopicId="template.par_moq">…children…</HelpTooltip>
 *     — wraps arbitrary children (rows, headers, plain buttons).
 *   <HelpTipButton helpTopicId="…" className=… onClick=…>label</HelpTipButton>
 *     — wrapper-free variant for buttons whose DOM position is asserted (the
 *     button stays exactly where a plain <button> would be).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getHelpTopic,
  requestHelpFromCounterIntelligence,
  trackHelpEvent,
  CI_HELP_ICON_SRC,
  TEMPLATES_PAGE,
} from '../utils/helpTopics';

// Above every app layer the tooltip must clear: modals and their backdrops
// (CustomModal 9999, confirmation overlays 10000), the Counterintelligence
// drawer (4001), and the sidebar (1101). Bubbles triggered INSIDE a modal
// (e.g. the start-choice modal) therefore render in front of it.
const TIP_Z_INDEX = 11000;
const TIP_WIDTH = 260;
const TIP_GAP = 8;
const VIEWPORT_MARGIN = 8;
// Rough height used only to decide above/below placement before first paint.
const TIP_ESTIMATED_HEIGHT = 170;
// Hover intent: the pointer must rest on the trigger this long before the
// tooltip opens, so users don't "trip" over popups while moving across the
// page. Deliberate interactions (click, tap, keyboard focus) stay immediate.
const HOVER_OPEN_DELAY_MS = 500;

let tipSeq = 0;
const nextTipId = () => `help-tip-${++tipSeq}`;

const bubbleShellStyle = {
  display: 'block',
  position: 'fixed',
  width: `${TIP_WIDTH}px`,
  backgroundColor: '#FFFDF9',
  border: '1px solid #D9CEC2',
  borderRadius: '10px',
  padding: '10px 12px',
  boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
  zIndex: TIP_Z_INDEX,
  textAlign: 'left',
  whiteSpace: 'normal',
  fontFamily: "'Open Sans', sans-serif",
};

const HelpTooltipBubble = React.forwardRef(function HelpTooltipBubble(
  { tipId, topic, position, onAskAi, onMouseEnter, onMouseLeave, ciButtonRef, onCiBlur, onEscape },
  ref,
) {
  return (
    <span
      ref={ref}
      role="tooltip"
      id={tipId}
      // Exempt from subscription-gate.js's write guard. On a blocked app path
      // (/items.html, /add-items.html, /count.html …) that guard swallows any
      // click whose label matches its write-verb regex, and the CI control's
      // name — "Ask Counterintelligence about Import a file" / "Send setup
      // email" / "Review & save" — matches. Reading an explanation is never a
      // write, and the bubble portals to document.body, so it escapes any
      // data-no-sub-guard container the trigger sits in and must carry its own.
      data-no-sub-guard="1"
      style={{
        ...bubbleShellStyle,
        left: `${position.left}px`,
        top: `${position.top}px`,
        // Above the trigger: anchor the bubble's bottom edge at `top`.
        transform: position.above ? 'translateY(-100%)' : 'none',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {topic.iconImg && (
        <img
          src={topic.iconImg.src}
          alt={topic.iconImg.alt}
          style={{ display: 'block', width: '170px', height: 'auto', margin: '0 auto 8px' }}
        />
      )}
      <span style={{ display: 'block', fontWeight: 700, fontSize: '0.78rem', color: '#3E2723', marginBottom: '3px' }}>
        {topic.label}
      </span>
      <span style={{ display: 'block', fontWeight: 400, fontSize: '0.72rem', lineHeight: 1.45, color: '#5A4A42' }}>
        {topic.tooltipText}
      </span>
      {topic.aiQuestion && (
        <span style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
          <button
            ref={ciButtonRef}
            type="button"
            aria-label={`Ask Counterintelligence about ${topic.label}`}
            onClick={onAskAi}
            onBlur={onCiBlur}
            onKeyDown={onEscape}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '3px',
              // Invisible padding keeps the touch target generous while the
              // visible control stays small and visually secondary.
              padding: '6px 4px 6px 8px', margin: '-6px -4px -6px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#6B4F3A', fontSize: '0.72rem', fontWeight: 700, lineHeight: 1,
            }}
          >
            <img
              src={CI_HELP_ICON_SRC}
              alt=""
              aria-hidden="true"
              style={{ width: '18px', height: '18px', borderRadius: '50%', display: 'block' }}
            />
            <span aria-hidden="true">?</span>
          </button>
        </span>
      )}
    </span>
  );
});

/**
 * Shared trigger logic. Returns the ref + handlers for the trigger element and
 * the portaled bubble element (null while closed).
 */
export function useHelpTooltip({
  helpTopicId,
  context,
  page = TEMPLATES_PAGE,
  section,
  placement = 'top',
}) {
  const topic = getHelpTopic(helpTopicId);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0, above: true });
  const [tipId] = useState(nextTipId);
  const triggerRef = useRef(null);
  const bubbleRef = useRef(null);
  const ciButtonRef = useRef(null);
  const hideTimerRef = useRef(null);
  const openTimerRef = useRef(null);

  const clearHideTimer = () => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
  };
  const clearOpenTimer = () => {
    if (openTimerRef.current) { clearTimeout(openTimerRef.current); openTimerRef.current = null; }
  };

  const hide = useCallback(() => {
    clearHideTimer();
    clearOpenTimer();
    setOpen(false);
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    clearOpenTimer(); // pointer left before the hover delay elapsed — never open
    hideTimerRef.current = setTimeout(() => setOpen(false), 120);
  }, []);

  const show = useCallback(() => {
    if (!topic) return;
    clearHideTimer();
    clearOpenTimer();
    const el = triggerRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.left + rect.width / 2 - TIP_WIDTH / 2, vw - TIP_WIDTH - VIEWPORT_MARGIN),
      );
      const above = placement === 'top' && rect.top > TIP_ESTIMATED_HEIGHT + TIP_GAP;
      const top = above ? rect.top - TIP_GAP : rect.bottom + TIP_GAP;
      setPosition({ left, top, above });
    }
    setOpen((was) => {
      if (!was) {
        trackHelpEvent('help_tooltip_opened', {
          help_topic_id: helpTopicId,
          page,
          template_type: context?.template_type,
        });
      }
      return true;
    });
  }, [topic, helpTopicId, page, context, placement]);

  // While open: Escape closes (never opens the tray), an outside pointer tap
  // closes, and any scroll/resize detaches the fixed bubble — close it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); hide(); } };
    const onPointerDown = (e) => {
      if (triggerRef.current?.contains(e.target) || bubbleRef.current?.contains(e.target)) return;
      hide();
    };
    const onScroll = () => hide();
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, hide]);

  useEffect(() => () => { clearHideTimer(); clearOpenTimer(); }, []);

  const askAi = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Close first so the tooltip never floats over the Counterintelligence tray.
    hide();
    requestHelpFromCounterIntelligence({ topicId: helpTopicId, page, section, context });
  }, [hide, helpTopicId, page, section, context]);

  // Hover only: wait out the hover-intent delay so passing the mouse over a
  // trigger on the way somewhere else never pops a tooltip. Deliberate
  // interactions (click, tap, keyboard focus) open immediately.
  const scheduleShow = useCallback(() => {
    if (!topic) return;
    clearHideTimer();
    clearOpenTimer();
    openTimerRef.current = setTimeout(show, HOVER_OPEN_DELAY_MS);
  }, [topic, show]);

  const triggerHandlers = {
    onMouseEnter: scheduleShow,
    onMouseLeave: scheduleHide,
    onFocus: show,
    onBlur: (e) => {
      const next = e.relatedTarget;
      if (next && (e.currentTarget.contains(next) || bubbleRef.current?.contains(next))) return;
      scheduleHide();
    },
    // Touch: the first tap opens the tooltip (and is swallowed so it doesn't
    // also fire the trigger's own action); the next tap acts normally.
    onTouchStart: (e) => {
      if (!open) {
        e.preventDefault();
        e.stopPropagation();
        show();
      }
    },
    onKeyDown: (e) => {
      if (e.key === 'Escape') { hide(); return; }
      // Tab moves into the bubble's CI help button; Shift+Tab leaves normally.
      if (e.key === 'Tab' && !e.shiftKey && open && ciButtonRef.current) {
        e.preventDefault();
        ciButtonRef.current.focus();
      }
    },
  };

  const bubble = (open && topic && typeof document !== 'undefined')
    ? createPortal(
      <HelpTooltipBubble
        ref={bubbleRef}
        tipId={tipId}
        topic={topic}
        position={{
          left: position.left,
          top: position.top,
          above: position.above,
        }}
        onAskAi={askAi}
        onMouseEnter={clearHideTimer}
        onMouseLeave={scheduleHide}
        ciButtonRef={ciButtonRef}
        onCiBlur={(e) => {
          const next = e.relatedTarget;
          if (next && (triggerRef.current?.contains(next) || bubbleRef.current?.contains(next))) return;
          hide();
        }}
        onEscape={(e) => { if (e.key === 'Escape') { e.preventDefault(); hide(); triggerRef.current?.focus?.(); } }}
      />,
      document.body,
    )
    : null;

  return { triggerRef, triggerHandlers, bubble, open, tipId, hide, show };
}

/**
 * Wrapper variant — for rows, headers, and other composite triggers.
 */
const HelpTooltip = ({ helpTopicId, context, page, section, placement, children }) => {
  const { triggerRef, triggerHandlers, bubble, open, tipId } = useHelpTooltip({
    helpTopicId, context, page, section, placement,
  });
  // Unknown/unset topic → render the trigger exactly as if unwrapped (no
  // handlers, no extra div) so call sites can conditionally assign topics.
  if (!getHelpTopic(helpTopicId)) return children;
  return (
    <div
      ref={triggerRef}
      style={{ position: 'relative' }}
      aria-describedby={open ? tipId : undefined}
      {...triggerHandlers}
    >
      {children}
      {bubble}
    </div>
  );
};

/**
 * Wrapper-free button variant — the button itself is the trigger, so it keeps
 * its exact place in the DOM (asserted for the template card's review action).
 */
export const HelpTipButton = ({ helpTopicId, context, page, section, placement, children, ...buttonProps }) => {
  const { triggerRef, triggerHandlers, bubble, open, tipId } = useHelpTooltip({
    helpTopicId, context, page, section, placement,
  });
  return (
    <button
      type="button"
      {...buttonProps}
      ref={(el) => {
        triggerRef.current = el;
        if (typeof buttonProps.ref === 'function') buttonProps.ref(el);
      }}
      aria-describedby={open ? tipId : undefined}
      {...triggerHandlers}
    >
      {children}
      {bubble}
    </button>
  );
};

export default HelpTooltip;

// One-time focus-style injection for the ⓘ trigger (inline styles can't
// express :focus-visible). Idempotent and jsdom-safe.
const HELP_INFO_STYLE_ID = 'help-info-btn-styles';
if (typeof document !== 'undefined' && !document.getElementById(HELP_INFO_STYLE_ID)) {
  const styleEl = document.createElement('style');
  styleEl.id = HELP_INFO_STYLE_ID;
  styleEl.textContent = '.help-info-btn:focus-visible{outline:2px solid #2E5E86;outline-offset:1px;border-radius:4px;}';
  document.head.appendChild(styleEl);
}

/**
 * HelpInfoButton — the small "ⓘ" trigger placed BESIDE a heading, tab label,
 * table column label, or badge (never nested inside another button). One
 * semantic button: hover, focus, click (Enter/Space), or first tap opens the
 * topic's tooltip; Escape closes it. aria-label follows the
 * "Learn about {label}" pattern.
 */
export const HelpInfoButton = ({ helpTopicId, context, page, section, placement, ariaLabel, style }) => {
  const topic = getHelpTopic(helpTopicId);
  const { triggerRef, triggerHandlers, bubble, open, tipId, hide, show } = useHelpTooltip({
    helpTopicId, context, page, section, placement,
  });
  if (!topic) return null;
  return (
    <button
      type="button"
      ref={triggerRef}
      aria-label={ariaLabel || `Learn about ${topic.label}`}
      aria-describedby={open ? tipId : undefined}
      // Same write-guard exemption as the bubble: "Learn about Import a file"
      // matches the guard's write-verb regex, but this trigger only opens a
      // tooltip. (Touch opens on touchstart, so only mouse/keyboard presses
      // reach the guard — which is exactly desktop.)
      data-no-sub-guard="1"
      onClick={(e) => {
        // Click/Enter/Space toggles — the one place a press is never swallowed.
        e.stopPropagation();
        if (open) hide(); else show();
      }}
      className="help-info-btn"
      {...triggerHandlers}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        // Invisible padding keeps the touch target generous while the visible
        // icon stays small and secondary.
        padding: '4px', margin: '-4px 0 -4px 2px',
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#A89890', fontSize: '0.72rem', lineHeight: 1,
        verticalAlign: 'middle',
        ...style,
      }}
    >
      <span aria-hidden="true">ⓘ</span>
      {bubble}
    </button>
  );
};

/**
 * Static-HTML bridge: mount HelpInfoButtons into declarative slots on pages
 * whose markup is plain HTML (e.g. items.html add-items cards). A slot is:
 *   <span data-help-topic="catalog.import_file"
 *         data-help-page="catalog" data-help-section="add_items"
 *         data-help-context='{"import_method":"file"}'></span>
 * Idempotent — a slot already mounted is skipped.
 */
export const mountHelpInfoSlots = (rootEl, React, createRoot) => {
  if (!rootEl || !React || !createRoot) return 0;
  const slots = rootEl.querySelectorAll('[data-help-topic]');
  let mounted = 0;
  slots.forEach((slot) => {
    if (slot._helpInfoRoot) return;
    const topicId = slot.getAttribute('data-help-topic');
    if (!getHelpTopic(topicId)) return;
    let context;
    try { context = JSON.parse(slot.getAttribute('data-help-context') || 'null') || undefined; }
    catch (_) { context = undefined; }
    const root = createRoot(slot);
    root.render(
      React.createElement(HelpInfoButton, {
        helpTopicId: topicId,
        page: slot.getAttribute('data-help-page') || undefined,
        section: slot.getAttribute('data-help-section') || undefined,
        context,
      }),
    );
    slot._helpInfoRoot = root;
    mounted += 1;
  });
  return mounted;
};
